import { useState, ChangeEvent, KeyboardEvent } from "react";
// import { motion } from "framer-motion";
import { Auth } from "aws-amplify";
import { AuthSignInSubPage, AuthSignInInput, AuthContentTitleWrapper, AuthContentTitle, AuthForgotPasswordButton, AuthContentSubTitle } from "../authBarStyling";
import { useToast } from "../../toast/ToastContext";
import ContinueBtn from "../../btn/continueBtn/continueBtn";
import { signInMenuActivatedSelector } from "../../../state/selectors/navbarToggle";
import { useRecoilState } from "recoil";
import { loginUser } from "../../../utils/auth/loginUser";
import { directToAfterLoginSelector } from "../../../state/selectors/directToAfterLogin";
import { useNavigate } from "react-router-dom";
import { googleLoginErrStateSelector } from "../../../state/selectors/googleLoginErr";
import styled from "styled-components";

const LogoImage = styled.img`
    position: absolute;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    height: 32px;
    width: auto;
    z-index: 103;

    @media (min-width: 768px) {
        top: 24px;
    }
`;

interface PasswordPopupProps {
    phoneNumFromPhonePopup: string;
    setLoggingIn: (loggingIn: boolean) => void;
    username: string;
    setPageName: (pageName: string) => void;
    setCodeDeliveryDetails: (codeDeliveryDetails: { AttributeName?: string; DeliveryMedium?: string; Destination?: string }) => void;
    setMfaInvalid: (mfaInvalid: boolean) => void;
    signInPasswordInvalid: boolean;
    setSignInPasswordInvalid: (signInPasswordInvalid: boolean) => void;
    handleKeyboardDismiss: () => void;
    isGoogleAccountExist: boolean;
}

const PasswordPopup: React.FC<PasswordPopupProps> = ({
    phoneNumFromPhonePopup,
    setLoggingIn,
    username,
    setPageName,
    setCodeDeliveryDetails,
    setMfaInvalid,
    signInPasswordInvalid,
    setSignInPasswordInvalid,
    handleKeyboardDismiss,
    isGoogleAccountExist,
}) => {
    const [passwordResetRequired, setPasswordResetRequired] = useState(false);
    const [user, setUser] = useState(false);
    const [password, setPassword] = useState("");
    const [directToAfterLogin, setDirectToAfterLogin] = useRecoilState(directToAfterLoginSelector);
    const [, setGoogleLoginErrState] = useRecoilState(googleLoginErrStateSelector);
    const [, setSignInMenuActivated] = useRecoilState(signInMenuActivatedSelector);
    const [passwordCannotBeEmpty, setPasswordCannotBeEmpty] = useState(false);
    const [isContinueBtnLoading, setIsContinueBtnLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const { showToast } = useToast();

    const signIn = async (password: string): Promise<void> => {
        try {
            if (password) {
                setPasswordCannotBeEmpty(false);
                setLoggingIn(true);
                setIsContinueBtnLoading(true);
                await Auth.signIn(username, password)
                    .then(async (user) => {
                        if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
                            setUser(user);
                            setPasswordResetRequired(true);
                            setPassword("");
                            setSignInPasswordInvalid(false);
                            setLoggingIn(false);
                            setIsContinueBtnLoading(false);
                        } else {
                            const { userHostModel } = await loginUser(user);
                            if (directToAfterLogin === "create-listing" && userHostModel) {
                                navigate(`/create-listing/${userHostModel.id}`);
                                setDirectToAfterLogin("");
                            }
                            setSignInPasswordInvalid(false);
                            setLoggingIn(false);
                            setGoogleLoginErrState("");
                            setSignInMenuActivated(false);
                        }
                    })
                    .catch(async (err) => {
                        console.log(err);
                        setLoggingIn(false);
                        setIsContinueBtnLoading(false);
                        if (err.code === "NotAuthorizedException") {
                            if (err.message === "User is disabled.") {
                                setPageName("AccountDeactivatedPopup");
                            } else {
                                showToast("Invalid password", "error");
                            }
                        } else if (err.code === "UserNotConfirmedException") {
                            setCodeDeliveryDetails({ Destination: phoneNumFromPhonePopup });
                            setPageName("SignUpVerificationPopup");
                            await Auth.resendSignUp(username)
                                .then((data) => {
                                    setCodeDeliveryDetails(data.CodeDeliveryDetails);
                                })
                                .catch((err) => {
                                    if (err.code === "CodeMismatchException") {
                                        setMfaInvalid(true);
                                    } else if (err.code === "LimitExceededException") {
                                        showToast("You exceeded your reset attempts, please try again later", "error");
                                    }
                                });
                        }
                        setSignInPasswordInvalid(true);
                    });
            } else {
                setPasswordCannotBeEmpty(true);
                setIsContinueBtnLoading(false);
                showToast("Password cannot be empty", "error");
            }
        } catch (err) {
            console.log(err);
        }
    };

    const resetPassword = async (newPassword: string) => {
        try {
            await Auth.completeNewPassword(user, newPassword, {})
                .then(async (user) => {
                    const { userHostModel } = await loginUser(user);
                    if (directToAfterLogin === "create-listing" && userHostModel) {
                        navigate(`/create-listing/${userHostModel.id}`);
                        setDirectToAfterLogin("");
                    }
                    setSignInPasswordInvalid(false);
                    setLoggingIn(false);
                    setGoogleLoginErrState("");
                    setSignInMenuActivated(false);
                })
                .catch(async (err) => {
                    console.log(err);
                    setLoggingIn(false);
                    setIsContinueBtnLoading(false);
                    if (err.code === "NotAuthorizedException") {
                        if (err.message === "User is disabled.") {
                            setPageName("AccountDeactivatedPopup");
                        }
                    } else if (err.code === "UserNotConfirmedException") {
                        setCodeDeliveryDetails({ Destination: phoneNumFromPhonePopup });
                        setPageName("SignUpVerificationPopup");
                        await Auth.resendSignUp(username)
                            .then((data) => {
                                setCodeDeliveryDetails(data.CodeDeliveryDetails);
                            })
                            .catch((err) => {
                                if (err.code === "CodeMismatchException") {
                                    setMfaInvalid(true);
                                } else if (err.code === "LimitExceededException") {
                                }
                            });
                    }
                    setSignInPasswordInvalid(true);
                });
        } catch (err) {
            console.log(err);
        }
    };

    return (
        <div>
            <LogoImage src="https://d292awxalydr86.cloudfront.net/Branding/Anystay.svg" alt="Anystay Logo" />
            <AuthSignInSubPage>
                <AuthContentTitleWrapper>
                    {!passwordResetRequired ? (
                        <>
                            <AuthContentTitle>Log in with your password</AuthContentTitle>
                            {isGoogleAccountExist && <AuthContentSubTitle>Account already exists, please enter your password to login</AuthContentSubTitle>}
                        </>
                    ) : (
                        <>
                            <AuthContentTitle>Create a new</AuthContentTitle>
                            <AuthContentTitle>permanent password</AuthContentTitle>
                        </>
                    )}
                </AuthContentTitleWrapper>
                <AuthSignInInput
                    value={password}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    invalid={signInPasswordInvalid || passwordCannotBeEmpty}
                    style={{ width: "100%", margin: "0", fontSize: "16px", borderRadius: "12px", marginBottom: "16px" }}
                    placeholder="Password"
                    type="password"
                    name="password"
                    onKeyDown={async (e: KeyboardEvent<HTMLInputElement>) => (e.key === "Enter" ? await signIn(password) : null)}
                    onBlur={handleKeyboardDismiss}
                />
                <ContinueBtn
                    continueType="button"
                    round={true}
                    isLoading={isContinueBtnLoading}
                    disabled={isContinueBtnLoading}
                    onClick={() => {
                        if (passwordResetRequired) {
                            resetPassword(password);
                        } else {
                            signIn(password);
                        }
                    }}
                    background={"black"}
                >
                    Continue
                </ContinueBtn>
                <div style={{ display: "flex", width: "100%", justifyContent: "center", marginTop: "16px" }}>
                    <AuthForgotPasswordButton
                        onClick={() => {
                            setPageName("NewPasswordPopup");
                            setSignInPasswordInvalid(false);
                        }}
                        type="button"
                    >
                        Forgot password?
                    </AuthForgotPasswordButton>
                </div>
            </AuthSignInSubPage>
        </div>
    );
};

export default PasswordPopup;
